import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Calendar,
  Clock,
  User,
  MapPin,
  Building,
  Tractor,
  Beaker,
  MessageSquare,
  AlertTriangle,
  Check,
  Euro,
  X,
  Plus,
  Save,
  FileText,
  Leaf,
  Scale,
  Edit3,
  Users,
  Search,
  Coffee,
  Car,
  Wrench,
  Hash,
  Ruler
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configuration des statuts de facturation
const STATUTS_FACTURATION = {
  facture: { label: 'Facturé', color: 'bg-green-100 text-green-700 border-green-300', icon: Check },
  a_facturer: { label: 'À facturer', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: Clock },
  non_facture: { label: 'Non facturé', color: 'bg-gray-100 text-gray-600 border-gray-300', icon: Euro },
  pas_de_facture: { label: 'Pas de facture', color: 'bg-red-100 text-red-700 border-red-300', icon: X }
};

// Configuration des catégories de remarques
const CATEGORIES_REMARQUES = {
  observation: { label: 'Observation', color: 'bg-blue-100 text-blue-700' },
  incident: { label: 'Incident', color: 'bg-orange-100 text-orange-700' },
  alerte: { label: 'Alerte', color: 'bg-red-100 text-red-700' },
  casse: { label: 'Casse', color: 'bg-purple-100 text-purple-700' }
};

// Modes de facturation
const MODES_FACTURATION = {
  heure: { label: 'À l\'heure', icon: Clock, unite: 'h' },
  hectare: { label: 'À l\'hectare', icon: Ruler, unite: 'ha' },
  nombre: { label: 'Au nombre', icon: Hash, unite: 'unités' }
};

export function FicheInterventionModal({ open, onClose, interventionId, onUpdate }) {
  const [intervention, setIntervention] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  // État pour les modifications
  const [editData, setEditData] = useState({});
  const [newProduit, setNewProduit] = useState({ nom: '', dose: '', unite_dose: 'L/ha', quantite_totale: '' });
  const [newRemarque, setNewRemarque] = useState({ texte: '', categorie: 'observation' });
  const [newMainOeuvre, setNewMainOeuvre] = useState({ nom: '', type_mo: 'saisonnier', heures: '', taux_horaire: '' });
  const [showAddProduit, setShowAddProduit] = useState(false);
  const [showAddRemarque, setShowAddRemarque] = useState(false);
  const [showAddMainOeuvre, setShowAddMainOeuvre] = useState(false);
  
  // Autocomplétion
  const [clientSearch, setClientSearch] = useState('');
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  
  const getToken = () => localStorage.getItem('token');
  const getSocieteId = () => localStorage.getItem('activeSocieteId');
  
  const fetchIntervention = useCallback(async () => {
    if (!interventionId) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/bot-interventions/intervention/${interventionId}`,
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'X-Societe-Id': getSocieteId()
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setIntervention(data.intervention);
        setEditData(data.intervention);
      }
    } catch (error) {
      console.error('Erreur chargement intervention:', error);
    } finally {
      setLoading(false);
    }
  }, [interventionId]);
  
  useEffect(() => {
    if (open && interventionId) {
      fetchIntervention();
    }
  }, [open, interventionId, fetchIntervention]);
  
  // Autocomplétion clients (3 lettres)
  useEffect(() => {
    const searchClients = async () => {
      if (clientSearch.length < 3) {
        setClientSuggestions([]);
        return;
      }
      
      try {
        const response = await fetch(
          `${API_URL}/api/bot-interventions/autocomplete/clients?q=${encodeURIComponent(clientSearch)}`,
          {
            headers: {
              'Authorization': `Bearer ${getToken()}`,
              'X-Societe-Id': getSocieteId()
            }
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          setClientSuggestions(data.clients || []);
          setShowClientSuggestions(true);
        }
      } catch (error) {
        console.error('Erreur autocomplétion:', error);
      }
    };
    
    const debounce = setTimeout(searchClients, 300);
    return () => clearTimeout(debounce);
  }, [clientSearch]);
  
  // Sélectionner un client depuis les suggestions
  const selectClient = (client) => {
    setEditData({
      ...editData, 
      client_id: client.id, 
      client_nom: client.nom
    });
    setClientSearch('');
    setShowClientSuggestions(false);
  };
  
  // Changer le statut de facturation
  const handleStatutFacturation = async (nouveauStatut) => {
    try {
      const response = await fetch(
        `${API_URL}/api/bot-interventions/intervention/${interventionId}/statut-facturation?statut=${nouveauStatut}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'X-Societe-Id': getSocieteId()
          }
        }
      );
      
      if (response.ok) {
        setIntervention(prev => ({ ...prev, statut_facturation: nouveauStatut }));
        setEditData(prev => ({ ...prev, statut_facturation: nouveauStatut }));
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      console.error('Erreur changement statut:', error);
    }
  };
  
  // Ajouter un produit
  const handleAddProduit = async () => {
    if (!newProduit.nom.trim()) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/bot-interventions/intervention/${interventionId}/produit`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getToken()}`,
            'X-Societe-Id': getSocieteId()
          },
          body: JSON.stringify({
            nom: newProduit.nom,
            dose: newProduit.dose ? parseFloat(newProduit.dose) : null,
            unite_dose: newProduit.unite_dose,
            quantite_totale: newProduit.quantite_totale ? parseFloat(newProduit.quantite_totale) : null
          })
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        setIntervention(prev => ({
          ...prev,
          produits: [...(prev.produits || []), { id: result.produit_id, ...newProduit }]
        }));
        setNewProduit({ nom: '', dose: '', unite_dose: 'L/ha', quantite_totale: '' });
        setShowAddProduit(false);
      }
    } catch (error) {
      console.error('Erreur ajout produit:', error);
    }
  };
  
  // Ajouter une remarque
  const handleAddRemarque = async () => {
    if (!newRemarque.texte.trim()) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/bot-interventions/intervention/${interventionId}/remarque`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getToken()}`,
            'X-Societe-Id': getSocieteId()
          },
          body: JSON.stringify(newRemarque)
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        setIntervention(prev => ({
          ...prev,
          remarques: [...(prev.remarques || []), { id: result.remarque_id, ...newRemarque, date_ajout: new Date().toISOString() }]
        }));
        setNewRemarque({ texte: '', categorie: 'observation' });
        setShowAddRemarque(false);
      }
    } catch (error) {
      console.error('Erreur ajout remarque:', error);
    }
  };
  
  // Ajouter main d'œuvre additionnelle
  const handleAddMainOeuvre = async () => {
    if (!newMainOeuvre.nom.trim() || !newMainOeuvre.heures) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/bot-interventions/intervention/${interventionId}/main-oeuvre`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getToken()}`,
            'X-Societe-Id': getSocieteId()
          },
          body: JSON.stringify({
            nom: newMainOeuvre.nom,
            type_mo: newMainOeuvre.type_mo,
            heures: parseFloat(newMainOeuvre.heures),
            taux_horaire: newMainOeuvre.taux_horaire ? parseFloat(newMainOeuvre.taux_horaire) : null
          })
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        const coutTotal = parseFloat(newMainOeuvre.heures) * (parseFloat(newMainOeuvre.taux_horaire) || 0);
        setIntervention(prev => ({
          ...prev,
          main_oeuvre_additionnelle: [...(prev.main_oeuvre_additionnelle || []), { 
            id: result.main_oeuvre_id, 
            ...newMainOeuvre,
            heures: parseFloat(newMainOeuvre.heures),
            cout_total: coutTotal
          }]
        }));
        setNewMainOeuvre({ nom: '', type_mo: 'saisonnier', heures: '', taux_horaire: '' });
        setShowAddMainOeuvre(false);
      }
    } catch (error) {
      console.error('Erreur ajout main oeuvre:', error);
    }
  };
  
  // Sauvegarder les modifications
  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/bot-interventions/intervention/${interventionId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getToken()}`,
            'X-Societe-Id': getSocieteId()
          },
          body: JSON.stringify(editData)
        }
      );
      
      if (response.ok) {
        setIntervention(editData);
        setEditMode(false);
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
    } finally {
      setSaving(false);
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  
  // Calculer le coût total de la main d'œuvre
  const getCoutMainOeuvre = () => {
    if (!intervention?.main_oeuvre_additionnelle) return 0;
    return intervention.main_oeuvre_additionnelle.reduce((sum, mo) => sum + (mo.cout_total || 0), 0);
  };
  
  if (!open) return null;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="fiche-intervention-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FileText className="h-6 w-6 text-green-600" />
            Fiche d'Intervention
            {intervention?.source === 'geofolia' && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Geofolia</span>
            )}
          </DialogTitle>
        </DialogHeader>
        
        {loading ? (
          <div className="p-8 text-center text-gray-500">
            <div className="animate-spin h-8 w-8 border-4 border-green-500 border-t-transparent rounded-full mx-auto mb-2"></div>
            Chargement...
          </div>
        ) : intervention ? (
          <div className="space-y-6">
            {/* Entête avec opération et statut */}
            <div className="flex flex-wrap justify-between items-start gap-4 pb-4 border-b">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {intervention.operation || intervention.description || 'Intervention'}
                </h2>
                <p className="text-gray-500 flex items-center gap-2 mt-1">
                  <Calendar className="h-4 w-4" />
                  {formatDate(intervention.date)}
                  {intervention.heure_debut && (
                    <span className="ml-2 flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {intervention.heure_debut}
                    </span>
                  )}
                </p>
              </div>
              
              {/* Bouton mode édition */}
              <Button
                variant={editMode ? "default" : "outline"}
                size="sm"
                onClick={() => setEditMode(!editMode)}
                className={editMode ? "bg-blue-600" : ""}
              >
                <Edit3 className="h-4 w-4 mr-1" />
                {editMode ? "Mode édition" : "Modifier"}
              </Button>
            </div>
            
            {/* Statut de facturation - 4 états cliquables */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Euro className="h-4 w-4 text-green-600" />
                  Statut de facturation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(STATUTS_FACTURATION).map(([key, config]) => {
                    const Icon = config.icon;
                    const isActive = intervention.statut_facturation === key;
                    return (
                      <button
                        key={key}
                        onClick={() => handleStatutFacturation(key)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                          isActive 
                            ? `${config.color} border-current ring-2 ring-offset-2` 
                            : 'bg-gray-50 text-gray-500 border-transparent hover:border-gray-300'
                        }`}
                        data-testid={`statut-btn-${key}`}
                      >
                        <Icon className="h-4 w-4" />
                        {config.label}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
            
            {/* Informations principales en grille */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Opérateur */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <User className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Opérateur</p>
                      <p className="font-semibold">{intervention.employe_nom || 'N/A'}</p>
                      {intervention.equipe && (
                        <p className="text-xs text-gray-500">{intervention.equipe.join(', ')}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Durée */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Clock className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Durée</p>
                      {editMode ? (
                        <input
                          type="number"
                          step="0.5"
                          value={editData.heures_travaillees || ''}
                          onChange={(e) => setEditData({...editData, heures_travaillees: parseFloat(e.target.value)})}
                          className="font-semibold text-lg w-20 p-1 border rounded"
                        />
                      ) : (
                        <p className="font-semibold text-lg">{intervention.heures_travaillees || intervention.duree || 0}h</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Client / Lieu avec autocomplétion */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <Building className="h-5 w-5 text-amber-600" />
                    </div>
                    <div className="flex-1 relative">
                      <p className="text-xs text-gray-500">
                        {intervention.type_activite === 'interne' ? 'Lieu' : 'Client'}
                      </p>
                      {editMode ? (
                        <div className="relative">
                          <div className="flex items-center gap-1">
                            <Search className="h-4 w-4 text-gray-400 absolute left-2 top-2.5" />
                            <input
                              type="text"
                              value={clientSearch || editData.client_nom || ''}
                              onChange={(e) => {
                                setClientSearch(e.target.value);
                                setEditData({...editData, client_nom: e.target.value});
                              }}
                              onFocus={() => clientSearch.length >= 3 && setShowClientSuggestions(true)}
                              placeholder="Tapez 3 lettres..."
                              className="font-semibold w-full p-1 pl-8 border rounded"
                              data-testid="client-autocomplete-input"
                            />
                          </div>
                          {/* Suggestions dropdown */}
                          {showClientSuggestions && clientSuggestions.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                              {clientSuggestions.map((client) => (
                                <button
                                  key={client.id}
                                  onClick={() => selectClient(client)}
                                  className="w-full text-left px-3 py-2 hover:bg-green-50 border-b last:border-b-0"
                                >
                                  <p className="font-medium">{client.nom}</p>
                                  {client.email && <p className="text-xs text-gray-500">{client.email}</p>}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="font-semibold">{intervention.client_nom || intervention.lieu || 'N/A'}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Matériel */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Tractor className="h-5 w-5 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-gray-500">Matériel</p>
                      {editMode ? (
                        <input
                          type="text"
                          value={editData.materiel || ''}
                          onChange={(e) => setEditData({...editData, materiel: e.target.value})}
                          className="font-semibold w-full p-1 border rounded"
                        />
                      ) : (
                        <p className="font-semibold">{intervention.materiel || 'N/A'}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* HEURES DÉTAILLÉES */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Clock className="h-4 w-4 text-blue-600" />
                  Heures détaillées
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Heures facturées */}
                  <div className="bg-green-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Euro className="h-3 w-3" /> Heures facturées
                    </p>
                    {editMode ? (
                      <input
                        type="number"
                        step="0.5"
                        value={editData.heures_facturees || ''}
                        onChange={(e) => setEditData({...editData, heures_facturees: parseFloat(e.target.value)})}
                        className="font-bold text-xl text-green-700 w-full bg-transparent border-b border-green-300"
                        placeholder="0"
                      />
                    ) : (
                      <p className="font-bold text-xl text-green-700">{intervention.heures_facturees || intervention.heures_travaillees || 0}h</p>
                    )}
                  </div>
                  
                  {/* Heures réelles */}
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <User className="h-3 w-3" /> Heures réelles
                    </p>
                    {editMode ? (
                      <input
                        type="number"
                        step="0.5"
                        value={editData.heures_reelles || ''}
                        onChange={(e) => setEditData({...editData, heures_reelles: parseFloat(e.target.value)})}
                        className="font-bold text-xl text-blue-700 w-full bg-transparent border-b border-blue-300"
                        placeholder="0"
                      />
                    ) : (
                      <p className="font-bold text-xl text-blue-700">{intervention.heures_reelles || intervention.heures_travaillees || 0}h</p>
                    )}
                  </div>
                  
                  {/* Pause midi */}
                  <div className="bg-amber-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Coffee className="h-3 w-3" /> Pause midi
                    </p>
                    {editMode ? (
                      <div className="flex gap-1 text-sm">
                        <input
                          type="time"
                          value={editData.pause_midi_debut || ''}
                          onChange={(e) => setEditData({...editData, pause_midi_debut: e.target.value})}
                          className="w-full bg-transparent border-b border-amber-300"
                        />
                        <span>-</span>
                        <input
                          type="time"
                          value={editData.pause_midi_fin || ''}
                          onChange={(e) => setEditData({...editData, pause_midi_fin: e.target.value})}
                          className="w-full bg-transparent border-b border-amber-300"
                        />
                      </div>
                    ) : (
                      <p className="text-sm text-amber-700">
                        {intervention.pause_midi_debut && intervention.pause_midi_fin 
                          ? `${intervention.pause_midi_debut} - ${intervention.pause_midi_fin}`
                          : intervention.duree_pause ? `${intervention.duree_pause}h` : 'Non renseigné'
                        }
                      </p>
                    )}
                  </div>
                  
                  {/* Déplacement / Entretien */}
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Car className="h-3 w-3" /> Déplacement
                    </p>
                    {editMode ? (
                      <input
                        type="number"
                        step="0.25"
                        value={editData.heures_deplacement || ''}
                        onChange={(e) => setEditData({...editData, heures_deplacement: parseFloat(e.target.value)})}
                        className="font-bold text-lg w-full bg-transparent border-b border-gray-300"
                        placeholder="0"
                      />
                    ) : (
                      <p className="font-bold text-lg">{intervention.heures_deplacement || 0}h</p>
                    )}
                    <p className="text-xs text-gray-500 flex items-center gap-1 mt-2">
                      <Wrench className="h-3 w-3" /> Entretien
                    </p>
                    {editMode ? (
                      <input
                        type="number"
                        step="0.25"
                        value={editData.heures_entretien || ''}
                        onChange={(e) => setEditData({...editData, heures_entretien: parseFloat(e.target.value)})}
                        className="font-bold text-lg w-full bg-transparent border-b border-gray-300"
                        placeholder="0"
                      />
                    ) : (
                      <p className="font-bold text-lg">{intervention.heures_entretien || 0}h</p>
                    )}
                  </div>
                </div>
                
                {/* Mode de facturation */}
                {editMode && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-2">Mode de facturation</p>
                    <div className="flex gap-2">
                      {Object.entries(MODES_FACTURATION).map(([key, config]) => {
                        const Icon = config.icon;
                        return (
                          <button
                            key={key}
                            onClick={() => setEditData({...editData, mode_facturation: key})}
                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm ${
                              (editData.mode_facturation || intervention.mode_facturation || 'heure') === key
                                ? 'bg-green-100 text-green-700 ring-2 ring-green-300'
                                : 'bg-white text-gray-600 border'
                            }`}
                          >
                            <Icon className="h-4 w-4" />
                            {config.label}
                          </button>
                        );
                      })}
                    </div>
                    {(editData.mode_facturation === 'hectare' || editData.mode_facturation === 'nombre') && (
                      <div className="mt-2 flex gap-2">
                        <input
                          type="number"
                          value={editData.quantite_facturee || ''}
                          onChange={(e) => setEditData({...editData, quantite_facturee: parseFloat(e.target.value)})}
                          placeholder={`Quantité (${MODES_FACTURATION[editData.mode_facturation]?.unite})`}
                          className="flex-1 p-2 border rounded"
                        />
                        <input
                          type="number"
                          value={editData.tarif_unitaire || ''}
                          onChange={(e) => setEditData({...editData, tarif_unitaire: parseFloat(e.target.value)})}
                          placeholder="Tarif unitaire €"
                          className="flex-1 p-2 border rounded"
                        />
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* MAIN D'ŒUVRE ADDITIONNELLE */}
            <Card>
              <CardHeader className="py-3 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Users className="h-4 w-4 text-purple-600" />
                  Main d'œuvre additionnelle
                  <span className="text-xs text-gray-400">(Saisonniers, prestataires)</span>
                </CardTitle>
                <Button size="sm" variant="outline" onClick={() => setShowAddMainOeuvre(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Ajouter
                </Button>
              </CardHeader>
              <CardContent>
                {intervention.main_oeuvre_additionnelle?.length > 0 ? (
                  <div className="space-y-2">
                    {intervention.main_oeuvre_additionnelle.map((mo, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-purple-50 border border-purple-200 p-3 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`px-2 py-0.5 rounded text-xs ${
                            mo.type_mo === 'saisonnier' ? 'bg-orange-100 text-orange-700' :
                            mo.type_mo === 'prestataire' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {mo.type_mo}
                          </div>
                          <div>
                            <p className="font-medium">{mo.nom}</p>
                            <p className="text-xs text-gray-500">{mo.heures}h {mo.taux_horaire && `× ${mo.taux_horaire}€/h`}</p>
                          </div>
                        </div>
                        {mo.cout_total > 0 && (
                          <p className="font-bold text-purple-700">{mo.cout_total.toFixed(2)}€</p>
                        )}
                      </div>
                    ))}
                    {/* Total coût main d'œuvre */}
                    {getCoutMainOeuvre() > 0 && (
                      <div className="text-right pt-2 border-t">
                        <p className="text-sm text-gray-500">Coût total main d'œuvre:</p>
                        <p className="font-bold text-lg text-purple-700">{getCoutMainOeuvre().toFixed(2)}€</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-400 text-center py-4">Aucune main d'œuvre additionnelle</p>
                )}
                
                {/* Formulaire ajout main d'œuvre */}
                {showAddMainOeuvre && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                    <h4 className="font-medium mb-3">Ajouter main d'œuvre</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <label className="text-xs text-gray-500">Nom *</label>
                        <input
                          type="text"
                          value={newMainOeuvre.nom}
                          onChange={(e) => setNewMainOeuvre({...newMainOeuvre, nom: e.target.value})}
                          className="w-full p-2 border rounded"
                          placeholder="Nom du saisonnier/prestataire"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Type</label>
                        <select
                          value={newMainOeuvre.type_mo}
                          onChange={(e) => setNewMainOeuvre({...newMainOeuvre, type_mo: e.target.value})}
                          className="w-full p-2 border rounded"
                        >
                          <option value="saisonnier">Saisonnier</option>
                          <option value="prestataire">Prestataire</option>
                          <option value="interim">Intérim</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Heures *</label>
                        <input
                          type="number"
                          step="0.5"
                          value={newMainOeuvre.heures}
                          onChange={(e) => setNewMainOeuvre({...newMainOeuvre, heures: e.target.value})}
                          className="w-full p-2 border rounded"
                          placeholder="8"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="text-xs text-gray-500">Taux horaire (€)</label>
                        <input
                          type="number"
                          step="0.5"
                          value={newMainOeuvre.taux_horaire}
                          onChange={(e) => setNewMainOeuvre({...newMainOeuvre, taux_horaire: e.target.value})}
                          className="w-full p-2 border rounded"
                          placeholder="12"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" onClick={handleAddMainOeuvre}>
                        <Check className="h-4 w-4 mr-1" />
                        Ajouter
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setShowAddMainOeuvre(false)}>
                        Annuler
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Parcelles */}
            {(intervention.parcelles?.length > 0 || intervention.parcelle_nom) && (
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-green-600" />
                    Parcelles
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {intervention.parcelles?.map((p, idx) => (
                      <div key={idx} className="bg-green-50 border border-green-200 px-3 py-2 rounded-lg">
                        <p className="font-medium text-green-800">{p.nom || p}</p>
                        {p.surface && <p className="text-xs text-green-600">{p.surface} ha</p>}
                        {p.culture && <p className="text-xs text-green-600">{p.culture}</p>}
                      </div>
                    ))}
                    {intervention.parcelle_nom && !intervention.parcelles?.length && (
                      <div className="bg-green-50 border border-green-200 px-3 py-2 rounded-lg">
                        <p className="font-medium text-green-800">{intervention.parcelle_nom}</p>
                        {intervention.surface && <p className="text-xs text-green-600">{intervention.surface} ha</p>}
                      </div>
                    )}
                  </div>
                  {intervention.surface_traitee && (
                    <p className="mt-2 text-sm text-gray-600">
                      <Scale className="h-4 w-4 inline mr-1" />
                      Surface traitée: <strong>{intervention.surface_traitee} ha</strong>
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
            
            {/* Produits utilisés - Pour le registre des traitements */}
            <Card>
              <CardHeader className="py-3 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Beaker className="h-4 w-4 text-blue-600" />
                  Produits utilisés
                  <span className="text-xs text-gray-400">(Registre des traitements)</span>
                </CardTitle>
                <Button size="sm" variant="outline" onClick={() => setShowAddProduit(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Ajouter
                </Button>
              </CardHeader>
              <CardContent>
                {intervention.produits?.length > 0 ? (
                  <div className="space-y-2">
                    {intervention.produits.map((produit, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-blue-50 border border-blue-200 p-3 rounded-lg">
                        <div>
                          <p className="font-medium text-blue-800">{produit.nom}</p>
                          <div className="flex gap-4 text-xs text-blue-600 mt-1">
                            {produit.dose && <span>Dose: {produit.dose} {produit.unite_dose}</span>}
                            {produit.quantite_totale && <span>Qté totale: {produit.quantite_totale} {produit.unite_totale || 'L'}</span>}
                            {produit.numero_lot && <span>Lot: {produit.numero_lot}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-center py-4">Aucun produit enregistré</p>
                )}
                
                {/* Formulaire ajout produit */}
                {showAddProduit && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                    <h4 className="font-medium mb-3">Ajouter un produit</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <label className="text-xs text-gray-500">Nom du produit *</label>
                        <input
                          type="text"
                          value={newProduit.nom}
                          onChange={(e) => setNewProduit({...newProduit, nom: e.target.value})}
                          className="w-full p-2 border rounded"
                          placeholder="Ex: Roundup, DAP 18-46..."
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Dose</label>
                        <div className="flex gap-1">
                          <input
                            type="number"
                            step="0.1"
                            value={newProduit.dose}
                            onChange={(e) => setNewProduit({...newProduit, dose: e.target.value})}
                            className="w-full p-2 border rounded"
                            placeholder="2.5"
                          />
                          <select
                            value={newProduit.unite_dose}
                            onChange={(e) => setNewProduit({...newProduit, unite_dose: e.target.value})}
                            className="p-2 border rounded"
                          >
                            <option value="L/ha">L/ha</option>
                            <option value="kg/ha">kg/ha</option>
                            <option value="g/ha">g/ha</option>
                            <option value="mL/ha">mL/ha</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Quantité totale</label>
                        <input
                          type="number"
                          step="0.1"
                          value={newProduit.quantite_totale}
                          onChange={(e) => setNewProduit({...newProduit, quantite_totale: e.target.value})}
                          className="w-full p-2 border rounded"
                          placeholder="50"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" onClick={handleAddProduit}>
                        <Check className="h-4 w-4 mr-1" />
                        Ajouter
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setShowAddProduit(false)}>
                        Annuler
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Remarques et incidents */}
            <Card>
              <CardHeader className="py-3 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-orange-600" />
                  Remarques & Incidents
                </CardTitle>
                <Button size="sm" variant="outline" onClick={() => setShowAddRemarque(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Ajouter
                </Button>
              </CardHeader>
              <CardContent>
                {intervention.remarques?.length > 0 ? (
                  <div className="space-y-2">
                    {intervention.remarques.map((remarque, idx) => (
                      <div key={idx} className={`p-3 rounded-lg border ${CATEGORIES_REMARQUES[remarque.categorie]?.color || 'bg-gray-100'}`}>
                        <div className="flex items-start gap-2">
                          {remarque.categorie === 'casse' || remarque.categorie === 'alerte' ? (
                            <AlertTriangle className="h-4 w-4 mt-0.5" />
                          ) : (
                            <MessageSquare className="h-4 w-4 mt-0.5" />
                          )}
                          <div className="flex-1">
                            <p>{remarque.texte}</p>
                            <p className="text-xs opacity-70 mt-1">
                              {CATEGORIES_REMARQUES[remarque.categorie]?.label}
                              {remarque.date_ajout && ` • ${new Date(remarque.date_ajout).toLocaleDateString('fr-FR')}`}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : intervention.commentaire ? (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p>{intervention.commentaire}</p>
                  </div>
                ) : (
                  <p className="text-gray-400 text-center py-4">Aucune remarque</p>
                )}
                
                {/* Formulaire ajout remarque */}
                {showAddRemarque && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                    <h4 className="font-medium mb-3">Ajouter une remarque</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-gray-500">Catégorie</label>
                        <select
                          value={newRemarque.categorie}
                          onChange={(e) => setNewRemarque({...newRemarque, categorie: e.target.value})}
                          className="w-full p-2 border rounded"
                        >
                          {Object.entries(CATEGORIES_REMARQUES).map(([key, config]) => (
                            <option key={key} value={key}>{config.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Texte *</label>
                        <textarea
                          value={newRemarque.texte}
                          onChange={(e) => setNewRemarque({...newRemarque, texte: e.target.value})}
                          className="w-full p-2 border rounded"
                          rows={3}
                          placeholder="Ex: Cassé 3 ceps de vigne, accroché un fil de fer..."
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" onClick={handleAddRemarque}>
                        <Check className="h-4 w-4 mr-1" />
                        Ajouter
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setShowAddRemarque(false)}>
                        Annuler
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Métadonnées */}
            {(intervention.campagne || intervention.culture || intervention.source) && (
              <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                {intervention.campagne && (
                  <span className="bg-gray-100 px-2 py-1 rounded">Campagne: {intervention.campagne}</span>
                )}
                {intervention.culture && (
                  <span className="bg-green-100 text-green-700 px-2 py-1 rounded flex items-center gap-1">
                    <Leaf className="h-3 w-3" />
                    {intervention.culture}
                  </span>
                )}
                {intervention.source && (
                  <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">Source: {intervention.source}</span>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            Intervention non trouvée
          </div>
        )}
        
        <DialogFooter>
          {editMode && (
            <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default FicheInterventionModal;
